import { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { Plus, Trash2, Pencil, Network } from 'lucide-react';
import { toast } from 'sonner';
import { ExportMenu } from '../components/ExportMenu';
import { ImportDialog } from '../components/ImportDialog';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { apiErrorMessage } from '../utils/requestTheme';

/** Master Departemen (khusus Admin) - pilihan wajib di form IPR & OPR. */
export default function Departments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(null); // {id?, nama}
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get('/departments');
      setItems(res.data);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Gagal memuat departemen'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.nama.trim()) return toast.error('Nama departemen wajib diisi');
    setSaving(true);
    try {
      if (form.id) await api.put(`/departments/${form.id}`, { nama: form.nama });
      else await api.post('/departments', { nama: form.nama });
      toast.success(form.id ? 'Departemen diperbarui' : 'Departemen ditambahkan');
      setForm(null);
      fetchItems();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Gagal menyimpan'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/departments/${toDelete.id}`);
      toast.success('Departemen dihapus');
      setToDelete(null);
      fetchItems();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Gagal menghapus'));
    }
  };

  const q = search.trim().toUpperCase();
  const shown = q ? items.filter((d) => d.nama.includes(q)) : items;

  return (
    <Layout>
      <div className="space-y-6" data-testid="departments-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Network className="h-8 w-8 text-slate-700" />
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Departemen</h1>
              <p className="text-slate-500">Daftar departemen untuk form Internal & Operasional Payment Request</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <ExportMenu table="departments" size="default" params={{ search }} />
            <ImportDialog type="departments" title="Departemen" size="default" onDone={fetchItems} />
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => setForm({ nama: '' })} data-testid="add-department">
              <Plus className="h-4 w-4 mr-2" /> Tambah Departemen
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <CardTitle>Daftar Departemen ({shown.length})</CardTitle>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari departemen..." className="max-w-xs" data-testid="department-search" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-slate-500">Memuat...</p>
            ) : (
              <div className="space-y-2">
                {shown.map((d, idx) => (
                  <div key={d.id} className="flex items-center justify-between gap-2 p-3 bg-slate-50 rounded-lg border hover:bg-slate-100" data-testid={`department-${d.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm text-slate-400 w-8">{idx + 1}.</span>
                      <span className="font-medium truncate">{d.nama}</span>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setForm({ id: d.id, nama: d.nama })} data-testid={`edit-department-${d.id}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => setToDelete(d)} data-testid={`delete-department-${d.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                {shown.length === 0 && <p className="text-center py-8 text-slate-500">Belum ada departemen</p>}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{form?.id ? 'Edit Departemen' : 'Tambah Departemen'}</DialogTitle>
            </DialogHeader>
            {form && (
              <form onSubmit={save} className="space-y-4">
                <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value.toUpperCase() })}
                  placeholder="Nama departemen (contoh: FINANCE)" className="uppercase" maxLength={80} required data-testid="department-name" />
                {form.id && <p className="text-xs text-slate-500">Request yang sudah dibuat tetap menyimpan nama departemen lama.</p>}
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={saving} data-testid="department-save">
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <DeleteConfirmDialog
          open={!!toDelete}
          onOpenChange={(o) => !o && setToDelete(null)}
          onConfirm={remove}
          title="Hapus Departemen"
          showReasonInput={false}
          description={toDelete ? `Hapus departemen "${toDelete.nama}"? Request lama tetap menyimpan namanya, tetapi departemen ini tidak bisa dipilih lagi.` : ''}
        />
      </div>
    </Layout>
  );
}
